import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";
import useTypewriter from "./useTypewriter";
import UserContext from "./UserContext";
import { useReactToPrint } from "react-to-print";
import { PrintableLabReport } from "./LabReportPDF";
import TrendsPanel from "./TrendsPanel";
import SendIcon from "@mui/icons-material/Send";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import HistoryIcon from "@mui/icons-material/History";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HomeIcon from "@mui/icons-material/Home";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stack,
  Switch,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import OpioidRiskReport from "./reports/OpioidRiskReport";
import PainPerceptionReport from "./reports/PainPerceptionReport";
import PgxReport from "./reports/PgxReport";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Shared between AIden (patient) and ClAIre (clinician) — both post to
// /analyze/{botType}_bot_typed. Proove-only, on purpose: this normal
// analysis screen should never offer another lab's report as an option
// (that's a distinct, separately-priced flow, not part of this dropdown).
//
// 2026-09-18 — AIden (patient) also offers an "Old Report" option per type,
// so a patient uploading a historical Proove report (any of the 3 types)
// gets a dropdown entry that actually matches what they're holding, instead
// of it silently being misjudged as the wrong report type. Opioid Risk and
// Pain Perception's old and new report layouts are similar enough that ONE
// extraction already handles both — their "Old Report" entries intentionally
// point at the exact same backend value as their non-"Old" counterpart, this
// is purely a discoverability label, not a different code path. Drug
// Metabolism's old layout is genuinely different (see
// _typed_vertex_extract_pgx_old_report on the backend) — its "Old Report"
// entry uses the distinct "pgx_proove_old" value that routes there.
//
// ClAIre (clinician) never gets "Old Report" options — clinicians only ever
// work from real, current-format reports, never an AI-converted old one.
// `value` is what the dropdown itself uses (must be unique per option, so
// the two "old" entries get their own distinct dropdown-only value); `sendAs`
// is the real report_type sent to the backend — falls back to `value` when
// absent (see the .append("report_type", ...) call site below).
const AIDEN_REPORT_TYPE_OPTIONS = [
  { value: "opioid_risk", label: "Proove Opioid Risk" },
  { value: "opioid_risk_old", label: "Proove Old Opioid Risk Report", sendAs: "opioid_risk" },
  { value: "pain_perception", label: "Proove Pain Perception" },
  { value: "pain_perception_old", label: "Proove Old Pain Perception Report", sendAs: "pain_perception" },
  { value: "pgx_proove", label: "Proove Drug Metabolism" },
  { value: "pgx_proove_old", label: "Proove Old Drug Metabolism Report" },
];
const CLAIRE_REPORT_TYPE_OPTIONS = [
  { value: "opioid_risk", label: "Proove Opioid Risk" },
  { value: "pain_perception", label: "Proove Pain Perception" },
  { value: "pgx_proove", label: "Proove Drug Metabolism" },
];
const REPORT_COMPONENTS = {
  opioid_risk: OpioidRiskReport,
  pain_perception: PainPerceptionReport,
  pgx: PgxReport,
};

const BRAND = {
  navy:      "#0a0e27",
  navyDark:  "#0f1233",
  gold:      "#637cdb",
  navyLight: "#8fa3e8",
  offWhite:  "#eef1f8",
  textDark:  "#0f1233",
  textMid:   "#5b6584",
};

const LANGUAGE_OPTIONS = [
  { value: "english",    label: "English",    lang: "en-US", voiceHint: "Google US English" },
  { value: "spanish",    label: "Español",    lang: "es-ES", voiceHint: "Google español" },
  { value: "french",     label: "Français",   lang: "fr-FR", voiceHint: "Google français" },
  { value: "german",     label: "Deutsch",    lang: "de-DE", voiceHint: "Google Deutsch" },
  { value: "portuguese", label: "Português",  lang: "pt-PT", voiceHint: "Google português" },
  { value: "chinese",    label: "中文",        lang: "zh-CN", voiceHint: "Google 普通话" },
  { value: "japanese",   label: "日本語",      lang: "ja-JP", voiceHint: "Google 日本語" },
  { value: "korean",     label: "한국어",      lang: "ko-KR", voiceHint: "Google 한국의" },
  { value: "hindi",      label: "हिन्दी",    lang: "hi-IN", voiceHint: "Google हिन्दी" },
  { value: "arabic",     label: "العربية",   lang: "ar-SA", voiceHint: "Google العربية" },
  { value: "russian",    label: "Русский",    lang: "ru-RU", voiceHint: "Google русский" },
  { value: "telugu",     label: "తెలుగు",    lang: "te-IN", voiceHint: "" },
  { value: "bengali",    label: "বাংলা",      lang: "bn-IN", voiceHint: "" },
  { value: "urdu",       label: "اردو",       lang: "ur-PK", voiceHint: "" },
  { value: "farsi",      label: "فارسی",      lang: "fa-IR", voiceHint: "" },
  { value: "vietnamese", label: "Tiếng Việt", lang: "vi-VN", voiceHint: "Google tiếng Việt" },
];

const PHASES = [
  { key: "uploading",   label: "Uploading & converting pages…",         pct: 20 },
  { key: "processing",  label: "Processing report layout…",             pct: 45 },
  { key: "analyzing",   label: "AI is reading every result…",           pct: 75 },
  { key: "summarizing", label: "Generating your personalised summary…", pct: 92 },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function loadVoices() {
  return new Promise((resolve) => {
    const voices = window.speechSynthesis.getVoices();
    if (voices.length) { resolve(voices); return; }
    const onChanged = () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChanged);
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener("voiceschanged", onChanged);
    setTimeout(() => resolve(window.speechSynthesis.getVoices()), 3000);
  });
}

function chunkText(text, maxChars = 200) {
  const sentences = text.replace(/([.!?])\s+/g, "$1\n").split("\n");
  const chunks = [];
  let current = "";
  for (const s of sentences) {
    if ((current + s).length > maxChars) {
      if (current) chunks.push(current.trim());
      current = s + " ";
    } else {
      current += s + " ";
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

function speakChunks(chunks, voice, lang, rate, onEnd) {
  if (!chunks.length) { onEnd(); return; }
  const [head, ...tail] = chunks;
  const utt = new SpeechSynthesisUtterance(head);
  if (voice) { utt.voice = voice; utt.lang = voice.lang; }
  else { utt.lang = lang; }
  utt.rate = rate;
  utt.onend  = () => speakChunks(tail, voice, lang, rate, onEnd);
  utt.onerror = () => speakChunks(tail, voice, lang, rate, onEnd);
  window.speechSynthesis.speak(utt);
}

function detectStatus(children) {
  const text = extractText(children);
  const hasHigh = /\bhigh\b|↑/i.test(text);
  const hasLow  = /\blow\b|↓/i.test(text);
  if (hasHigh && hasLow) return null;
  if (hasHigh) return "high";
  if (hasLow)  return "low";
  if (/\bcritical\b/i.test(text)) return "critical";
  if (/\bnormal\b/i.test(text))   return "normal";
  return null;
}

function extractText(node) {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (node?.props?.children) return extractText(node.props.children);
  return "";
}

// ─────────────────────────────────────────────────────────────────────────────
//  ANIMATED TOAST SYSTEM
// ─────────────────────────────────────────────────────────────────────────────
const TOAST_CONFIG = {
  success: {
    icon: <CheckCircleIcon sx={{ fontSize: 17 }} />,
    accent: "#22c55e",
    border: "rgba(34,197,94,0.28)",
    bg: "rgba(34,197,94,0.08)",
  },
  error: {
    icon: <ErrorIcon sx={{ fontSize: 17 }} />,
    accent: "#ef4444",
    border: "rgba(239,68,68,0.28)",
    bg: "rgba(239,68,68,0.08)",
  },
  warning: {
    icon: <WarningAmberIcon sx={{ fontSize: 17 }} />,
    accent: "#f59e0b",
    border: "rgba(245,158,11,0.28)",
    bg: "rgba(245,158,11,0.08)",
  },
};

function ToastItem({ toast, onRemove, isDark }) {
  const cfg = TOAST_CONFIG[toast.type] || TOAST_CONFIG.success;
  const [progress, setProgress] = useState(100);
  const duration = toast.duration ?? 5000;

  useEffect(() => {
    const start = Date.now();
    const frame = () => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / duration) * 100));
      if (elapsed < duration) requestAnimationFrame(frame);
    };
    const raf = requestAnimationFrame(frame);
    const timeout = setTimeout(() => onRemove(toast.id), duration);
    return () => { cancelAnimationFrame(raf); clearTimeout(timeout); };
  }, [toast.id, duration, onRemove]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 60, scale: 0.9 }}
      animate={{ opacity: 1, x: 0,  scale: 1 }}
      exit={{ opacity: 0, x: 60, scale: 0.88, transition: { duration: 0.18 } }}
      transition={{ type: "spring", stiffness: 450, damping: 34 }}
      style={{ position: "relative" }}
    >
      <Box sx={{
        display: "flex", alignItems: "flex-start", gap: 1.25,
        minWidth: 290, maxWidth: 400,
        bgcolor: isDark ? "rgba(15,23,42,0.94)" : "rgba(255,255,255,0.97)",
        backdropFilter: "blur(24px) saturate(1.5)",
        border: `1px solid ${cfg.border}`,
        borderRadius: "14px",
        px: 1.75, pt: 1.4, pb: 1.8,
        boxShadow: isDark
          ? "0 12px 40px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04)"
          : "0 12px 40px rgba(10,14,39,0.14), 0 1px 0 rgba(255,255,255,1)",
        overflow: "hidden",
        position: "relative",
      }}>
        <Box sx={{ color: cfg.accent, flexShrink: 0, mt: "2px" }}>{cfg.icon}</Box>
        <Typography sx={{
          flex: 1, fontSize: 12.5, lineHeight: 1.55, fontWeight: 500,
          color: isDark ? "#e2e6f0" : "#1e293b",
        }}>
          {toast.message}
        </Typography>
        <motion.div whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
          <IconButton size="small" onClick={() => onRemove(toast.id)} sx={{
            p: 0.3, mt: "-2px", color: isDark ? "#475569" : "#94a3b8",
            "&:hover": { color: isDark ? "#cbd5e1" : "#475569", bgcolor: "transparent" },
          }}>
            <CloseRoundedIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </motion.div>
        {/* Progress bar */}
        <Box sx={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 2,
          bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
        }}>
          <Box sx={{
            height: "100%", bgcolor: cfg.accent,
            width: `${progress}%`,
            transition: "width 0.05s linear",
            borderRadius: "0 0 2px 2px",
            opacity: 0.7,
          }} />
        </Box>
      </Box>
    </motion.div>
  );
}

function ToastContainer({ toasts, onRemove, isDark }) {
  return (
    <Box sx={{
      position: "fixed", bottom: 24, right: 24, zIndex: 9999,
      display: "flex", flexDirection: "column-reverse", gap: 1,
      pointerEvents: "none",
      "& > *": { pointerEvents: "auto" },
    }}>
      <AnimatePresence mode="popLayout">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onRemove={onRemove} isDark={isDark} />
        ))}
      </AnimatePresence>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONFIRM MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmModal({
  open, onConfirm, onCancel, isDark,
  icon, tone = "danger", title = "Clear all saved memory?",
  body = <>All saved analyses for this assistant will be permanently deleted.
    Trend detection resets to zero.{" "}
    <Box component="span" sx={{ fontWeight: 700, color: "#ef4444" }}>This cannot be undone.</Box></>,
  confirmLabel = "Clear Memory",
}) {
  const toneColor = tone === "danger" ? "#ef4444" : "#637cdb";
  const toneColorDark = tone === "danger" ? "#dc2626" : "#3F6AC0";
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed", inset: 0, zIndex: 1400,
              background: "rgba(2,6,23,0.6)",
              backdropFilter: "blur(12px)",
            }}
            onClick={onCancel}
          />
          <motion.div
            key="card"
            initial={{ opacity: 0, scale: 0.84, y: 32 }}
            animate={{ opacity: 1, scale: 1,    y: 0 }}
            exit={{   opacity: 0, scale: 0.88,  y: 20, transition: { duration: 0.18 } }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            style={{
              position: "fixed", inset: 0, zIndex: 1401,
              display: "flex", alignItems: "center", justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Box sx={{
              pointerEvents: "auto",
              bgcolor: isDark ? "rgba(10,18,40,0.97)" : "rgba(255,255,255,0.98)",
              backdropFilter: "blur(32px) saturate(1.6)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.1)"}`,
              borderRadius: "22px",
              p: "28px 30px 26px",
              maxWidth: 400, width: "calc(100vw - 48px)",
              boxShadow: isDark
                ? "0 48px 120px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.05), inset 0 1px 0 rgba(255,255,255,0.06)"
                : "0 48px 120px rgba(10,14,39,0.22), inset 0 1px 0 rgba(255,255,255,1)",
            }}>
              {/* Icon badge */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.08, type: "spring", stiffness: 500, damping: 28 }}
              >
                <Box sx={{
                  width: 52, height: 52, borderRadius: "16px", mb: 2.5,
                  background: `linear-gradient(135deg, ${toneColor}26, ${toneColorDark}14)`,
                  border: `1px solid ${toneColor}40`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {icon || <DeleteSweepIcon sx={{ color: toneColor, fontSize: 26 }} />}
                </Box>
              </motion.div>

              <Typography sx={{
                fontSize: 17.5, fontWeight: 800,
                color: isDark ? "#f1f5f9" : "#0f1233",
                letterSpacing: "-0.01em", mb: 1,
              }}>
                {title}
              </Typography>
              <Typography sx={{
                fontSize: 13.5, color: isDark ? "#94a3b8" : "#475569",
                lineHeight: 1.72, mb: 3.5,
              }}>
                {body}
              </Typography>

              <Stack direction="row" spacing={1.25}>
                <motion.div style={{ flex: 1 }} whileTap={{ scale: 0.97 }}>
                  <Button fullWidth onClick={onCancel} sx={{
                    borderRadius: "12px", fontSize: 13.5, fontWeight: 600, py: 1.2,
                    color: isDark ? "#94a3b8" : "#64748b",
                    bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                    "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)" },
                    transition: "all 0.18s",
                  }}>
                    Cancel
                  </Button>
                </motion.div>
                <motion.div style={{ flex: 1 }} whileTap={{ scale: 0.97 }}>
                  <Button fullWidth onClick={onConfirm} variant="contained" sx={{
                    borderRadius: "12px", fontSize: 13.5, fontWeight: 700, py: 1.2,
                    background: `linear-gradient(135deg, ${toneColor}, ${toneColorDark})`,
                    boxShadow: `0 4px 20px ${toneColor}66`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${toneColor}, ${toneColorDark})`,
                      boxShadow: `0 8px 28px ${toneColor}80`,
                      transform: "translateY(-1px)",
                    },
                    transition: "all 0.2s",
                  }}>
                    {confirmLabel}
                  </Button>
                </motion.div>
              </Stack>
            </Box>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MARKDOWN COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const markdownComponents = (isDark, textPrimary, textSecondary, border, BRAND) => ({
  h1: ({ children }) => (
    <Box sx={{
      bgcolor: BRAND.navy, color: "#fff", px: 2.5, py: 1.5,
      borderRadius: 1, mt: 3.5, mb: 2,
      fontFamily: "Michroma",
      fontWeight: 800, fontSize: 20, letterSpacing: "0.01em",
    }}>
      {children}
    </Box>
  ),
  h2: ({ children }) => (
    <Box sx={{
      display: "flex", alignItems: "stretch",
      mt: 3, mb: 1.5, borderRadius: "0 6px 6px 0",
      overflow: "hidden",
      bgcolor: isDark ? "#1e3a5f" : "#edf2fc",
      border: `1px solid ${isDark ? "#2d4f7c" : "#d0ddf5"}`,
    }}>
      <Box sx={{ width: 4, flexShrink: 0, bgcolor: BRAND.gold }} />
      <Typography sx={{
        px: 2, py: 1.25,
        fontFamily: "Michroma",
        fontWeight: 700, fontSize: 17,
        color: isDark ? "#93c5fd" : BRAND.navy,
        lineHeight: 1.4,
      }}>
        {children}
      </Typography>
    </Box>
  ),
  h3: ({ children }) => (
    <Typography sx={{
      fontWeight: 700, fontSize: 15,
      color: isDark ? "#cbd5e1" : BRAND.navy,
      mt: 2.5, mb: 0.75,
      borderBottom: `1px solid ${isDark ? "#334155" : "#e2e6f0"}`,
      pb: 0.5,
    }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography component="p" sx={{
      mb: 1.5, lineHeight: 1.9, fontSize: 15, color: textPrimary,
    }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{
      pl: 2, mb: 1,
      listStyleType: "none",
      "& li::marker": { display: "none" },
    }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, mb: 1 }}>
      {children}
    </Box>
  ),
  li: ({ children, ordered, index }) => {
    const status = detectStatus(children);
    const bulletColor = {
      normal:   "#16a34a",
      high:     "#dc2626",
      low:      "#b47800",
      critical: "#9333ea",
    }[status] || BRAND.navy;
    const bullet = ordered ? `${(index ?? 0) + 1}.` : "•";
    return (
      <Box component="li" sx={{
        display: "flex", alignItems: "flex-start",
        gap: 1.25, mb: 0.9, listStyle: "none",
      }}>
        <Box component="span" sx={{
          flexShrink: 0, mt: "3px", minWidth: 16,
          fontWeight: 700, fontSize: 15,
          color: bulletColor, lineHeight: 1.65,
        }}>
          {bullet}
        </Box>
        <Box sx={{ flex: 1, fontSize: 15, color: textPrimary, lineHeight: 1.85 }}>
          {children}
        </Box>
      </Box>
    );
  },
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700, color: isDark ? "#f1f5f9" : BRAND.navy }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ color: textSecondary, fontStyle: "italic" }}>
      {children}
    </Box>
  ),
  code: ({ children, isInline }) => (
    <Box
      component={isInline ? "code" : "pre"}
      sx={{
        fontFamily: "monospace", fontSize: 13,
        px: 0.75, py: 0.2, borderRadius: 0.5,
        bgcolor: isDark ? "#0f172a" : "#f1f5f9",
        color: isDark ? "#7dd3fc" : BRAND.navy,
        border: `1px solid ${border}`,
        display: isInline ? "inline" : "block",
        whiteSpace: isInline ? "normal" : "pre-wrap",
        my: isInline ? 0 : 1,
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box sx={{
      my: 3, height: "1px", border: "none",
      background: `linear-gradient(to right, ${BRAND.gold}, transparent)`,
    }} />
  ),
  blockquote: ({ children }) => (
    <Box component="blockquote" sx={{
      borderLeft: `4px solid ${BRAND.gold}`,
      pl: 2, my: 2,
      bgcolor: isDark ? "#0f172a" : "#fdf8f3",
      py: 1.5, pr: 2,
      borderRadius: "0 8px 8px 0",
      "& p": { color: textSecondary, fontSize: 14, mb: 0, lineHeight: 1.75 },
    }}>
      {children}
    </Box>
  ),
});

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function BotInterface({ botType }) {
  const isPatient = botType === "patient";
  const botLabel  = isPatient ? "AIden" : "ClAIre";
  const botAccent = isPatient ? BRAND.gold : BRAND.navyLight;
  // AIden (patient) offers "Old Report" entries per type; ClAIre (clinician)
  // never does — see the comment above AIDEN_REPORT_TYPE_OPTIONS.
  const reportTypeOptions = isPatient ? AIDEN_REPORT_TYPE_OPTIONS : CLAIRE_REPORT_TYPE_OPTIONS;

  const [files, setFiles]                   = useState([]);
  const [previews, setPreviews]             = useState([]);
  const [analyzing, setAnalyzing]           = useState(false);
  // Separate from `analyzing` (the initial upload/typed-analyze flow, which
  // gates whether the report/answer result blocks render at all) — a
  // follow-up question in flight must NOT hide the already-rendered report.
  const [asking, setAsking]                 = useState(false);
  const [uploadPhase, setUploadPhase]       = useState("");
  const [answer, setAnswer]                 = useState("");
  const [followUps, setFollowUps]           = useState([]);
  const [question, setQuestion]             = useState("");
  const [toasts, setToasts]                 = useState([]);
  const [history, setHistory]               = useState([]);
  const [isSpeaking, setIsSpeaking]         = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  const [useAnimation, setUseAnimation]     = useState(true);
  // No setter — the "Long-term memory" toggle that used to flip this was
  // removed (report_type is now mandatory for both AIden and ClAIre, so the
  // generic/trend-memory flow this gated is unreachable), but useMemory is
  // still read a couple of places below in that same now-dormant flow.
  const [useMemory]                         = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [dragOver, setDragOver]             = useState(false);
  const [reportMetadata, setReportMetadata] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [analysisTimestamp, setAnalysisTimestamp] = useState("");
  const [trendsData, setTrendsData]         = useState(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  // Typed report analysis (Opioid Risk / Pain Perception / PGX) — available
  // to both AIden (patient) and ClAIre (clinician).
  const [reportType, setReportType]         = useState("");
  const [typedReport, setTypedReport]       = useState(null);
  // AIden's ad-hoc analysis is ephemeral — nothing here gets persisted.
  // Making a report "current" for combined analysis/monthly digest happens
  // exclusively via Monthly Update Sources (Report Dashboard), never from
  // this chat flow.

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const printRef    = useRef(null);

  const { user } = useContext(UserContext);
  const authHeaders = useMemo(
    () => (user?.token ? { Authorization: `Bearer ${user.token}` } : {}),
    [user?.token]
  );

  const analyzeEndpoint = `${API_BASE}/analyze/${botType}_bot`;
  const askEndpoint = useMemory
    ? `${API_BASE}/ask/memory_${botType}_bot`
    : `${API_BASE}/ask/${botType}_bot`;

  const langConfig = LANGUAGE_OPTIONS.find((l) => l.value === selectedLanguage) || LANGUAGE_OPTIONS[0];
  const phase = PHASES.find((p) => p.key === uploadPhase) || { label: "Working…", pct: 50 };

  const bg            = isDark ? "#070d1f" : "#f0f4ff";
  const sidebar       = isDark ? "rgba(15,24,48,0.85)" : "rgba(255,255,255,0.88)";
  const border        = isDark ? "#1e2d50" : "#e2e6f0";
  const textPrimary   = isDark ? "#e8edf5" : BRAND.textDark;
  const textSecondary = isDark ? "#8899b8" : "#475569";

  // ── Toast helpers ─────────────────────────────────────────────────────────
  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, ...toast }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // ── Speech synthesis ─────────────────────────────────────────────────────
  const handleToggleReadAloud = useCallback(async () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    if (!answer) return;
    const voices  = await loadVoices();
    const matched =
      voices.find((v) => langConfig.voiceHint && v.name.includes(langConfig.voiceHint)) ||
      voices.find((v) => v.lang.startsWith(langConfig.lang.slice(0, 2))) ||
      null;
    if (!matched && !voices.length) {
      addToast({ type: "error", message: "Read-aloud is not available in this browser." });
      return;
    }
    const readContent = followUps.length > 0 ? followUps[followUps.length - 1].content : answer;
    const plain  = readContent.replace(/[*#_`~]/g, "").replace(/\n+/g, " ");
    const chunks = chunkText(plain, 220);
    setIsSpeaking(true);
    speakChunks(chunks, matched, langConfig.lang, 1.0, () => setIsSpeaking(false));
  }, [answer, isSpeaking, langConfig, followUps, addToast]);

  // ── File handling ─────────────────────────────────────────────────────────
  const processFiles = (selected) => {
    const filtered = Array.from(selected).filter((f) =>
      ["application/pdf", "image/png", "image/jpeg", "image/jpg"].includes(f.type)
    );
    setFiles(filtered);
    setPreviews(
      filtered.map((f) => ({
        name: f.name,
        type: f.type,
        url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      }))
    );
  };
  const handleFileChange = (e) => processFiles(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  // ── Fetch trends ──────────────────────────────────────────────────────────
  const fetchTrends = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/trends/${botType}_bot`, { headers: authHeaders });
      if (res.data?.charts?.length > 0) setTrendsData(res.data);
      else setTrendsData(null);
    } catch {
      setTrendsData(null);
    }
  }, [botType, authHeaders]);

  // ── Analyze ───────────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!files.length) return;
    if (!reportType) {
      addToast({ type: "error", message: "Please select a report type before analyzing." });
      return;
    }
    setAnalyzing(true);
    setAnswer("");
    setHistory([]);
    setReportMetadata([]);
    setTrendsData(null);
    setTypedReport(null);
    // 2026-09-21 fix: this used to leave a previous analysis's follow-up
    // Q&A thread on screen under a brand-new report — nothing here ever
    // cleared it, so a Drug Metabolism follow-up could still be showing
    // after switching to an Opioid Risk analysis.
    setFollowUps([]);

    // Typed report path (Opioid Risk / Pain Perception / PGX) — a single
    // call to /analyze/{botType}_bot_typed does classify + extract +
    // summarize server-side, for both AIden (patient) and ClAIre
    // (clinician); only the summary's tone/depth differs server-side.
    if (reportType) {
      try {
        setUploadPhase("uploading");
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        const selectedOption = reportTypeOptions.find((t) => t.value === reportType);
        formData.append("report_type", (selectedOption && selectedOption.sendAs) || reportType);
        formData.append("language", langConfig.label);

        setUploadPhase("analyzing");
        const res = await axios.post(`${API_BASE}/analyze/${botType}_bot_typed`, formData, { headers: authHeaders });

        setUploadPhase("summarizing");
        await new Promise((r) => setTimeout(r, 300));

        setTypedReport(res.data);
        setAnalysisTimestamp(new Date().toLocaleString());
      } catch (err) {
        addToast({ type: "error", message: err.response?.data?.detail || err.message || "Analysis failed." });
      } finally {
        setAnalyzing(false);
        setUploadPhase("");
      }
      return;
    }

    try {
      setUploadPhase("uploading");
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      await axios.post(analyzeEndpoint, formData, { headers: authHeaders });

      setUploadPhase("processing");
      await new Promise((r) => setTimeout(r, 400));

      setUploadPhase("analyzing");
      const res = await axios.post(
        askEndpoint,
        { question: "__AUTO_SUMMARY_VISION__", target_language: selectedLanguage },
        { headers: authHeaders }
      );

      setUploadPhase("summarizing");
      await new Promise((r) => setTimeout(r, 300));

      const summary = res.data.answer;
      setAnswer(summary);
      setFollowUps([]);
      setReportMetadata(res.data.metadata || []);
      setHistory([{ type: "Initial Analysis", content: summary }]);
      setAnalysisTimestamp(new Date().toLocaleString());
      if (useMemory) fetchTrends();
    } catch (err) {
      addToast({ type: "error", message: err.response?.data?.detail || err.message || "Analysis failed." });
    } finally {
      setAnalyzing(false);
      setUploadPhase("");
    }
  };

  // ── Follow-up ─────────────────────────────────────────────────────────────
  // 2026-09-21 fix: /ask/{bot}_bot used to rely entirely on a server-side
  // "structured cache" file for prior context — which nothing in the typed-
  // report pipeline (or anywhere else) ever actually wrote, so every
  // follow-up question was answered with zero knowledge of the report just
  // analyzed. Simplest robust fix, no new persistence/session machinery
  // needed: the frontend already HAS the full context in memory right after
  // analysis (typedReport / answer) — just send it along with each
  // question. Stateless and correct by construction: whatever's on screen
  // is exactly what gets sent, every time, so there's no separate cache to
  // go stale or leak between reports.
  const stripHtmlToText = (html) =>
    (html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const handleAskQuestion = async () => {
    if (!question.trim()) return;
    if (question.trim().length > 2000) {
      addToast({ type: "error", message: "Question is too long (max 2000 characters)." });
      return;
    }
    setAsking(true);
    const q = question.trim();
    setQuestion("");
    const priorAnalysis = typedReport
      ? stripHtmlToText(typedReport.clinical_summary_html)
      : stripHtmlToText(answer);
    try {
      const res = await axios.post(
        askEndpoint,
        { question: q, target_language: selectedLanguage, prior_analysis: priorAnalysis },
        { headers: authHeaders }
      );
      const response = res.data.answer;
      setFollowUps((prev) => [...prev, { question: q, content: response }]);
      setHistory((prev) => [...prev, { type: q, content: response }]);
    } catch (err) {
      addToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to get answer." });
    } finally {
      setAsking(false);
    }
  };

  // ── Memory ────────────────────────────────────────────────────────────────
  // handleSaveToMemory (posted to /add_to_memory/{bot}_bot) and
  // handleClearMemory were only ever called from the "Long-term memory"
  // sidebar toggle/buttons, which were removed above (report_type is now
  // mandatory for both personas, so that generic/trend flow is
  // unreachable) — deleted as genuinely dead code rather than left orphaned.
  // confirmClearOpen/handleConfirmClear stay, since the dialog itself is
  // still wired into the (dormant) generic-answer view further below.
  const handleConfirmClear = async () => {
    setConfirmClearOpen(false);
    setAnalyzing(true);
    try {
      await axios.post(`${API_BASE}/clear_${botType}_bot_memories`, {}, { headers: authHeaders });
      addToast({ type: "success", message: "Memory cleared successfully." });
      setHistory([]);
      setTrendsData(null);
    } catch (err) {
      addToast({ type: "error", message: err.response?.data?.detail || "Failed to clear memory." });
    } finally {
      setAnalyzing(false);
    }
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Proove ${botLabel} Report - ${new Date().toLocaleDateString()}`,
    onBeforePrint: async () => setDownloadingPdf(true),
    onAfterPrint:  () => setDownloadingPdf(false),
  });
  const handleDownloadPDF = () => { if (answer) handlePrint(); };

  // ── Cleanup ───────────────────────────────────────────────────────────────
  useEffect(
    () => () => previews.forEach((p) => p.url && URL.revokeObjectURL(p.url)),
    [previews]
  );
  useEffect(() => {
    const stop = () => { if (isSpeaking) { window.speechSynthesis.cancel(); setIsSpeaking(false); } };
    window.addEventListener("beforeunload", stop);
    return () => window.removeEventListener("beforeunload", stop);
  }, [isSpeaking]);

  const typedAnswer = useTypewriter(answer, 12);

  // ── Gradient border helper ────────────────────────────────────────────────
  const zoneBg = isDark ? "#0a1020" : "#f5f8ff";
  const zoneBorderGradient = dragOver
    ? `linear-gradient(${zoneBg}, ${zoneBg}) padding-box, linear-gradient(135deg, ${BRAND.navy}, ${BRAND.gold}, ${BRAND.navyLight}) border-box`
    : files.length
      ? `linear-gradient(${zoneBg}, ${zoneBg}) padding-box, linear-gradient(135deg, ${BRAND.gold}77, ${BRAND.navy}77) border-box`
      : `linear-gradient(${zoneBg}, ${zoneBg}) padding-box, linear-gradient(135deg, ${border}, ${border}) border-box`;

  // ==========================================================================
  //  RENDER
  // ==========================================================================
  return (
    <Box sx={{
      height: "100vh", display: "flex", flexDirection: "column",
      overflow: "hidden", bgcolor: bg,
      fontFamily: "'Metropolis', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* ── Header ── */}
      <Box component="header" sx={{
        background: `linear-gradient(to right, ${BRAND.navyDark}, ${BRAND.navy})`,
        borderBottom: `2px solid ${BRAND.gold}44`,
        flexShrink: 0,
        boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
      }}>
        <Box sx={{ px: 3, height: 64, display: "flex", alignItems: "center",
          justifyContent: "space-between", maxWidth: 1440, mx: "auto" }}>
          <Box component={Link} to="/home"
            sx={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img src="/logo-light.png" alt="Proove Genomics" style={{ height: 20, width: "auto" }} />
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Live badge */}
            <Box sx={{
              display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1,
              bgcolor: `${botAccent}18`,
              border: `1px solid ${botAccent}44`,
              borderRadius: "10px", px: 1.25, py: 0.4,
              backdropFilter: "blur(8px)",
            }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                border: `2px solid rgba(99,124,219,0.55)`,
                boxShadow: "0 0 10px rgba(0,0,0,0.35)",
              }}>
                <img
                  src={isPatient ? "/aiden-avatar.png" : "/claire-avatar.png"}
                  alt={botLabel}
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 12%" }}
                />
              </Box>
              <Box sx={{
                width: 7, height: 7, borderRadius: "50%", bgcolor: botAccent,
                "@keyframes pulse": { "0%,100%": { opacity: 1, transform: "scale(1)" }, "50%": { opacity: 0.4, transform: "scale(0.8)" } },
                animation: "pulse 2s infinite",
              }} />
              <Typography sx={{ fontFamily: "Michroma", fontSize: 15, fontWeight: 700, letterSpacing: "0.08em", color: "rgba(255,255,255,0.92)" }}>
                {isPatient ? (
                  <><Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>den</>
                ) : (
                  <>Cl<Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>re</>
                )}
              </Typography>
            </Box>
            <ThemeToggle />
            {/* Home button */}
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
              <Button
                onClick={() => navigate("/home")}
                size="small"
                startIcon={<HomeIcon sx={{ fontSize: "15px !important" }} />}
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: 13, fontWeight: 600, px: 1.5, py: 0.6,
                  borderRadius: "8px", textTransform: "none", minWidth: 0,
                  border: "1px solid transparent",
                  transition: "all 0.2s",
                  "&:hover": {
                    color: BRAND.gold,
                    bgcolor: "rgba(99,124,219,0.1)",
                    border: `1px solid ${BRAND.gold}33`,
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Home</Box>
              </Button>
            </motion.div>
            <NavUserMenu />
          </Stack>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", maxWidth: 1440, mx: "auto", width: "100%" }}>

        {/* ─── LEFT SIDEBAR ─── */}
        <motion.div
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          style={{ width: 320, flexShrink: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
          <Box sx={{
            width: "100%", flexShrink: 0,
            bgcolor: sidebar,
            backdropFilter: "blur(24px) saturate(1.4)",
            borderRight: { md: `1px solid ${border}` },
            display: "flex", flexDirection: "column", overflowY: "auto",
            height: "100%",
          }}>
            {/* Sidebar header */}
            <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${border}`, flexShrink: 0 }}>
              <Typography sx={{ fontFamily: "Michroma", fontSize: 15, fontWeight: 800, letterSpacing: "0.12em", color: isDark ? "rgba(255,255,255,0.92)" : BRAND.navy, mb: 0.4 }}>
                {isPatient ? (
                  <><Box component="span" sx={{ color: BRAND.gold }}>AI</Box>den — Patient Assistant</>
                ) : (
                  <>Cl<Box component="span" sx={{ color: BRAND.gold }}>AI</Box>re — Decision Support</>
                )}
              </Typography>
              <Typography sx={{ fontSize: 15, color: textSecondary, lineHeight: 1.6 }}>
                {isPatient
                  ? "Upload a lab report for a plain-language interpretation of every result."
                  : "Upload a patient report for evidence-based clinical analysis with citations."}
              </Typography>
            </Box>

            {/* Upload drop zone */}
            <Box sx={{ px: 3, pt: 3 }}>
              <motion.div
                animate={{ scale: dragOver ? 1.015 : 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 28 }}
              >
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDragEnd={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    background: zoneBorderGradient,
                    border: "2px solid transparent",
                    borderRadius: "16px", p: 3, textAlign: "center", cursor: "pointer",
                    bgcolor: zoneBg,
                    boxShadow: dragOver
                      ? `0 0 0 6px rgba(10,14,39,0.1), 0 8px 32px rgba(10,14,39,0.15)`
                      : files.length
                        ? `0 0 0 3px rgba(99,124,219,0.12)`
                        : "none",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      boxShadow: `0 0 0 4px rgba(99,124,219,0.14), 0 4px 20px rgba(10,14,39,0.1)`,
                    },
                  }}
                >
                  <input ref={fileInputRef} type="file" hidden multiple accept=".pdf,image/*" onChange={handleFileChange} />
                  <motion.div
                    animate={{ scale: dragOver ? 1.22 : 1, rotate: dragOver ? 5 : 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 22 }}
                  >
                    <UploadFileIcon sx={{
                      fontSize: 38, mb: 1,
                      color: dragOver ? BRAND.navy : files.length ? BRAND.navy : BRAND.gold,
                      filter: dragOver ? `drop-shadow(0 0 10px ${BRAND.navy}88)` : "none",
                      transition: "color 0.2s",
                    }} />
                  </motion.div>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: textPrimary, mb: 0.5 }}>
                    {dragOver
                      ? "Release to upload"
                      : files.length > 0
                        ? `${files.length} file${files.length > 1 ? "s" : ""} selected`
                        : "Drop files or click to upload"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: textSecondary }}>PDF · PNG · JPG</Typography>
                </Box>
              </motion.div>

              {/* Proove-only disclaimer */}
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                <Box sx={{
                  mt: 1.75, px: 1.5, py: 1.1,
                  bgcolor: isDark ? "rgba(99,124,219,0.07)" : "rgba(99,124,219,0.1)",
                  border: `1px solid ${isDark ? "rgba(99,124,219,0.18)" : "rgba(99,124,219,0.32)"}`,
                  borderRadius: "10px",
                  display: "flex", alignItems: "flex-start", gap: 1,
                }}>
                  <Typography sx={{ color: BRAND.gold, fontSize: 12, flexShrink: 0, mt: "1px", lineHeight: 1 }}>⚠</Typography>
                  <Typography sx={{ fontSize: 13, color: textSecondary, lineHeight: 1.55 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: isDark ? BRAND.gold : BRAND.navy }}>
                      Proove-affiliated reports only.
                    </Box>{" "}
                    Accurate analysis is designed for Proove lab reports. Other formats may produce incomplete or incorrect results.
                    Like all AI, outputs may occasionally contain errors — always verify values with your healthcare provider.
                  </Typography>
                </Box>
              </motion.div>

              {/* File chips */}
              <AnimatePresence>
                {previews.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {previews.map((file, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          transition={{ type: "spring", stiffness: 400, damping: 24 }}
                        >
                          <Tooltip title={file.name} placement="top" arrow>
                            <Box onClick={(e) => e.stopPropagation()} sx={{
                              display: "flex", alignItems: "center", gap: 0.75,
                              bgcolor: isDark ? "rgba(15,24,48,0.8)" : BRAND.offWhite,
                              border: `1px solid ${border}`,
                              boxShadow: `0 2px 8px rgba(0,0,0,0.06)`,
                              borderRadius: "10px", px: 1.25, py: 0.5,
                              transition: "box-shadow 0.15s",
                              "&:hover": { boxShadow: `0 4px 16px rgba(10,14,39,0.12)` },
                            }}>
                              {file.url
                                ? <img src={file.url} alt="" style={{ width: 22, height: 22, borderRadius: 4, objectFit: "cover" }} />
                                : <Box sx={{ width: 22, height: 22, background: `linear-gradient(135deg, ${BRAND.navy}, #28336b)`,
                                    borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Typography sx={{ color: "#fff", fontSize: 7, fontWeight: 800 }}>PDF</Typography>
                                  </Box>
                              }
                              <Typography sx={{ fontSize: 11, color: textPrimary, maxWidth: 110,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {file.name}
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.25, cursor: "pointer", color: textSecondary, fontSize: 16, lineHeight: 1,
                                  width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                                  borderRadius: "50%", transition: "all 0.15s",
                                  "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.12)" },
                                }}
                                onClick={() => {
                                  setFiles(files.filter((_, i) => i !== idx));
                                  setPreviews(previews.filter((_, i) => i !== idx));
                                }}>×</Box>
                            </Box>
                          </Tooltip>
                        </motion.div>
                      ))}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            {/* Settings */}
            <Box sx={{ px: 3, pt: 3, pb: 2 }}>
              {/* Section divider */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
                <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
                <Typography sx={{
                  fontSize: 11, fontWeight: 800, letterSpacing: "0.18em",
                  color: textSecondary, textTransform: "uppercase", flexShrink: 0,
                }}>Configuration</Typography>
                <Box sx={{ flex: 1, height: "1px", bgcolor: border }} />
              </Box>

              {/* Language select */}
              <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em",
                  color: textSecondary, textTransform: "uppercase", mb: 0.75 }}>
                  Response language
                </Typography>
                <Select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  size="small" fullWidth
                  MenuProps={{ slotProps: { paper: { sx: {
                    borderRadius: "14px", mt: 0.75,
                    boxShadow: isDark
                      ? "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)"
                      : "0 16px 48px rgba(10,14,39,0.16)",
                    bgcolor: isDark ? "#0f1830" : "#fff",
                    border: `1px solid ${border}`,
                  }}}}}
                  sx={{
                    fontSize: 13.5, fontWeight: 600, borderRadius: "10px",
                    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.03)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: border, borderWidth: "1.5px",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${botAccent}88` },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: botAccent, borderWidth: "2px",
                      boxShadow: `0 0 0 3px ${botAccent}20`,
                    },
                    "& .MuiSelect-select": { py: 1, px: 1.5 },
                    color: textPrimary,
                    transition: "all 0.2s",
                  }}
                >
                  {LANGUAGE_OPTIONS.map((l) => (
                    <MenuItem key={l.value} value={l.value} sx={{ fontSize: 13.5, fontWeight: 600, borderRadius: "8px", mx: 0.5 }}>{l.label}</MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Report type — shared between AIden (patient) and ClAIre (clinician) */}
              {(
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.1em",
                    color: textSecondary, textTransform: "uppercase", mb: 0.75 }}>
                    Report type
                  </Typography>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    displayEmpty
                    size="small" fullWidth
                    MenuProps={{ slotProps: { paper: { sx: {
                      borderRadius: "14px", mt: 0.75,
                      boxShadow: isDark
                        ? "0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)"
                        : "0 16px 48px rgba(10,14,39,0.16)",
                      bgcolor: isDark ? "#0f1830" : "#fff",
                      border: `1px solid ${border}`,
                    }}}}}
                    sx={{
                      fontSize: 13.5, fontWeight: 600, borderRadius: "10px",
                      bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.03)",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: border, borderWidth: "1.5px" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: `${botAccent}88` },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: botAccent, borderWidth: "2px",
                        boxShadow: `0 0 0 3px ${botAccent}20`,
                      },
                      "& .MuiSelect-select": { py: 1, px: 1.5 },
                      color: textPrimary,
                      transition: "all 0.2s",
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: 13.5 }}>Select a report type…</MenuItem>
                    {reportTypeOptions.map((t) => (
                      <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13.5, fontWeight: 600, borderRadius: "8px", mx: 0.5 }}>{t.label}</MenuItem>
                    ))}
                  </Select>
                  <Typography sx={{ fontSize: 11, color: textSecondary, mt: 0.75, lineHeight: 1.5 }}>
                    This analysis is for your own Proove Genomics reports only. For another lab's Drug Metabolism/PGX report, use the separate paid upload option from registration instead.
                  </Typography>
                </Box>
              )}

              {/* Toggles */}
              <Stack spacing={1}>
                {[
                  {
                    checked: useAnimation,
                    onChange: setUseAnimation,
                    label: "Typing animation",
                    sub: "Stream response character by character",
                  },
                ].map(({ checked, onChange, label, sub, badge, tooltip }) => (
                  <Tooltip key={label} title={tooltip || ""} placement="right" disableHoverListener={!tooltip}>
                    <motion.div whileHover={{ scale: 1.01 }} transition={{ type: "spring", stiffness: 400, damping: 28 }}>
                      <Box
                        onClick={() => onChange(!checked)}
                        sx={{
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          bgcolor: checked
                            ? (isDark ? `${botAccent}14` : `${botAccent}0e`)
                            : (isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
                          border: `1px solid ${checked ? `${botAccent}44` : border}`,
                          borderRadius: "12px", px: 1.75, py: 1,
                          cursor: "pointer",
                          transition: "all 0.22s ease",
                          "&:hover": {
                            bgcolor: checked
                              ? (isDark ? `${botAccent}1e` : `${botAccent}16`)
                              : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"),
                          },
                        }}
                      >
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: textPrimary, lineHeight: 1.2 }}>
                              {label}
                            </Typography>
                            <AnimatePresence>
                              {badge && (
                                <motion.div
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{ type: "spring", stiffness: 500, damping: 24 }}
                                >
                                  <Box sx={{
                                    fontSize: 8.5, fontWeight: 800, letterSpacing: "0.08em",
                                    color: "#fff", bgcolor: botAccent, borderRadius: "5px",
                                    px: 0.7, py: 0.15, lineHeight: 1.5,
                                  }}>{badge}</Box>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </Box>
                          <Typography sx={{ fontSize: 12, color: textSecondary, mt: 0.2 }}>{sub}</Typography>
                        </Box>
                        <Switch
                          checked={checked}
                          size="small"
                          onChange={(e) => { e.stopPropagation(); onChange(e.target.checked); }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiSwitch-track": {
                              bgcolor: checked ? `${botAccent} !important` : undefined,
                              opacity: checked ? "0.9 !important" : undefined,
                              transition: "background-color 0.3s ease",
                            },
                            "& .MuiSwitch-thumb": {
                              bgcolor: "#fff",
                              boxShadow: checked ? `0 0 6px ${botAccent}88` : "0 1px 3px rgba(0,0,0,0.2)",
                              transition: "box-shadow 0.3s ease",
                            },
                          }}
                        />
                      </Box>
                    </motion.div>
                  </Tooltip>
                ))}
              </Stack>
            </Box>

            {/* Primary CTA */}
            <Box sx={{ px: 3, pb: 3, mt: "auto" }}>
              <motion.div whileTap={{ scale: 0.98 }}>
                <Button
                  fullWidth variant="contained"
                  onClick={handleUpload}
                  disabled={analyzing || !files.length || !reportType}
                  startIcon={analyzing
                    ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                    : <UploadFileIcon sx={{ fontSize: "18px !important" }} />}
                  sx={{
                    background: (!analyzing && files.length)
                      ? `linear-gradient(135deg, ${BRAND.navy} 0%, #28336b 100%)`
                      : undefined,
                    bgcolor: (!analyzing && files.length) ? undefined : (isDark ? "#1e2d50" : "#cbd5e1"),
                    color: "#fff",
                    fontWeight: 700, py: 1.5, fontSize: 15,
                    letterSpacing: "0.04em", borderRadius: "12px",
                    boxShadow: (!analyzing && files.length)
                      ? "0 4px 20px rgba(10,14,39,0.4)"
                      : "none",
                    position: "relative", overflow: "hidden",
                    "&:hover": {
                      background: (!analyzing && files.length)
                        ? `linear-gradient(135deg, #28336b 0%, ${BRAND.navy} 100%)`
                        : undefined,
                      transform: "translateY(-1px)",
                      boxShadow: "0 8px 28px rgba(10,14,39,0.45)",
                    },
                    "&:disabled": {
                      color: isDark ? "#475569" : "#94a3b8",
                    },
                    transition: "all 0.22s ease",
                    // Shimmer sweep
                    "@keyframes shimmer": {
                      "0%":   { left: "-80%" },
                      "100%": { left: "140%" },
                    },
                    "&::after": files.length ? {
                      content: '""',
                      position: "absolute",
                      top: 0, bottom: 0,
                      left: "-80%",
                      width: "50%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)",
                      transform: "skewX(-20deg)",
                      animation: "shimmer 3s ease-in-out infinite",
                    } : {},
                  }}
                >
                  {analyzing ? phase.label : "Analyze Report"}
                </Button>
              </motion.div>

              {/* Progress bar */}
              <AnimatePresence>
                {analyzing && (
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0.8 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box sx={{ mt: 1.5, borderRadius: "6px", overflow: "hidden", height: 5,
                      bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.08)" }}>
                      <motion.div
                        animate={{ width: `${phase.pct}%` }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        style={{
                          height: "100%",
                          background: `linear-gradient(to right, ${BRAND.navy}, ${botAccent}, ${BRAND.gold})`,
                          borderRadius: 6,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)",
                          backgroundSize: "200% 100%",
                          "@keyframes pgShimmer": {
                            "0%":   { backgroundPosition: "-200% 0" },
                            "100%": { backgroundPosition: "200% 0" },
                          },
                          animation: "pgShimmer 1.8s linear infinite",
                        }} />
                      </motion.div>
                    </Box>
                    <Typography sx={{ fontSize: 12, color: textSecondary, mt: 0.6, textAlign: "right" }}>
                      {phase.pct}%
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

          </Box>
        </motion.div>

        {/* ─── MAIN CONTENT ─── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <AnimatePresence mode="wait">

            {/* Empty state */}
            {!answer && !analyzing && !typedReport && (
              <motion.div
                key="empty"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.4 }}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}
              >
                <Box sx={{
                  flex: 1, overflowY: "auto", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", p: 6, textAlign: "center",
                }}>
                  <motion.div
                    animate={{ y: [0, -9, 0] }}
                    transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Box sx={{
                      width: 96, height: 96,
                      background: isDark
                        ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                        : "linear-gradient(135deg, #fff 0%, #eef3ff 100%)",
                      border: `1.5px solid ${border}`,
                      borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mb: 3.5,
                      boxShadow: isDark
                        ? `0 12px 40px rgba(0,0,0,0.4), 0 0 0 12px rgba(10,14,39,0.06)`
                        : `0 12px 40px rgba(10,14,39,0.1), 0 0 0 12px rgba(10,14,39,0.04)`,
                    }}>
                      <UploadFileIcon sx={{ fontSize: 40, color: BRAND.gold }} />
                    </Box>
                  </motion.div>

                  <Typography sx={{ fontFamily: "Michroma", fontSize: 22, fontWeight: 800, color: textPrimary, mb: 1, letterSpacing: "-0.01em" }}>
                    {isPatient ? "Upload your lab report" : "Upload a patient report"}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: textSecondary, maxWidth: 380, lineHeight: 1.8 }}>
                    {isPatient
                      ? "Select a report type and a PDF or image of your lab results on the left, then click Analyze Report."
                      : "Select a report type and the patient's lab report PDF or image on the left. AI will generate a full clinical interpretation."}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, mt: 4.5, flexWrap: "wrap", justifyContent: "center" }}>
                    {(isPatient
                      ? ["Plain-language results", "Dietary & lifestyle advice", "Questions for your doctor", "16 languages"]
                      : ["Risk stratification", "Evidence-based treatment", "PubMed citations", "Cross-report correlation"]
                    ).map((f, i) => (
                      <motion.div
                        key={f}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + i * 0.08, duration: 0.35 }}
                      >
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 0.75,
                          bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(10,14,39,0.04)",
                          border: `1px solid ${border}`,
                          borderRadius: "99px", px: 1.75, py: 0.6,
                          backdropFilter: "blur(8px)",
                        }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: BRAND.gold, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: 12, color: textSecondary, fontWeight: 500 }}>{f}</Typography>
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </Box>
              </motion.div>
            )}

            {/* Loading state */}
            {analyzing && (
              <motion.div
                key="loading"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.28 }}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}
              >
                <Box sx={{
                  flex: 1, overflowY: "auto",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", p: 6, textAlign: "center",
                }}>
                  {/* Orbital spinner */}
                  <Box sx={{ position: "relative", width: 96, height: 96, mb: 4 }}>
                    {/* Outer ring */}
                    <Box sx={{
                      position: "absolute", inset: 0,
                      borderRadius: "50%",
                      border: `2px solid ${isDark ? "#1e2d50" : "#e2e6f0"}`,
                    }} />
                    {/* Spinning arc */}
                    <Box sx={{
                      position: "absolute", inset: 0, borderRadius: "50%",
                      border: "2px solid transparent",
                      borderTopColor: BRAND.navy,
                      borderRightColor: `${BRAND.navy}44`,
                      "@keyframes spinArc": { to: { transform: "rotate(360deg)" } },
                      animation: "spinArc 1.1s linear infinite",
                    }} />
                    {/* Counter-rotating arc */}
                    <Box sx={{
                      position: "absolute", inset: 8, borderRadius: "50%",
                      border: "2px solid transparent",
                      borderBottomColor: BRAND.gold,
                      borderLeftColor: `${BRAND.gold}44`,
                      "@keyframes spinArcRev": { to: { transform: "rotate(-360deg)" } },
                      animation: "spinArcRev 1.6s linear infinite",
                    }} />
                    {/* Center pulsing orb */}
                    <Box sx={{
                      position: "absolute",
                      top: "50%", left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}>
                      <motion.div
                        animate={{ scale: [1, 0.78, 1], opacity: [0.9, 0.6, 0.9] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                      >
                        <Box sx={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: `radial-gradient(circle at 35% 35%, ${BRAND.gold}, ${BRAND.gold}88)`,
                          boxShadow: `0 0 16px ${BRAND.gold}66`,
                        }} />
                      </motion.div>
                    </Box>
                  </Box>

                  <AnimatePresence mode="wait">
                    <motion.div
                      key={uploadPhase}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.22 }}
                    >
                      <Typography sx={{ fontFamily: "Michroma", fontSize: 18, fontWeight: 700, color: textPrimary, mb: 0.75, letterSpacing: "-0.01em" }}>
                        {phase.label}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: textSecondary, mb: 4, minHeight: 22 }}>
                        {uploadPhase === "uploading"   && "Converting your document to high-resolution images…"}
                        {uploadPhase === "processing"  && "Detecting report layout and cropping sections…"}
                        {uploadPhase === "analyzing"   && "AI is interpreting every test value and reference range…"}
                        {uploadPhase === "summarizing" && "Formatting your personalised summary…"}
                        {!uploadPhase && "Please wait…"}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>

                  {/* Progress track */}
                  <Box sx={{ width: "100%", maxWidth: 440 }}>
                    <Box sx={{
                      height: 6, borderRadius: 99, overflow: "hidden",
                      bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(10,14,39,0.08)",
                    }}>
                      <motion.div
                        animate={{ width: `${phase.pct}%` }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        style={{
                          height: "100%",
                          background: `linear-gradient(to right, ${BRAND.navy}, ${botAccent}, ${BRAND.gold})`,
                          borderRadius: 99,
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                          backgroundSize: "200% 100%",
                          "@keyframes pgShimmer2": {
                            "0%":   { backgroundPosition: "-200% 0" },
                            "100%": { backgroundPosition: "200% 0" },
                          },
                          animation: "pgShimmer2 1.8s linear infinite",
                        }} />
                      </motion.div>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
                      <Typography sx={{ fontSize: 10, color: textSecondary }}>{phase.label.replace("…","")}</Typography>
                      <Typography sx={{ fontSize: 10, color: textSecondary, fontWeight: 700 }}>{phase.pct}%</Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            )}

            {/* Typed report result — Opioid Risk / Pain Perception / PGX,
                shared between AIden (patient) and ClAIre (clinician).
                Renders the branded report template instead of a plain-text
                answer; follow-up questions below reuse the existing
                question/handleAskQuestion/followUps state, since the images
                are already cached server-side from the typed-analyze call. */}
            {typedReport && !analyzing && (
              <motion.div
                key="typedResult"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                style={{ flex: 1, overflow: "auto" }}
              >
                <Box sx={{ px: 3, py: 3 }}>
                  {(() => {
                    const ReportComponent = REPORT_COMPONENTS[typedReport.report_type];
                    if (!ReportComponent) return null;
                    return (
                      <ReportComponent
                        extracted={typedReport.extracted}
                        clinicalSummaryHtml={typedReport.clinical_summary_html}
                        disclaimer={typedReport.disclaimer}
                        mismatchNotice={typedReport.mismatch_notice}
                        reportDate={analysisTimestamp || new Date().toLocaleDateString()}
                        analyzedBy={botLabel}
                      />
                    );
                  })()}

                  {/* AIden's ad-hoc analysis is ephemeral by design — nothing
                      here gets persisted. To make a report the linked source
                      for combined analysis/monthly digest, use Monthly
                      Update Sources from the Report Dashboard instead. */}
                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: textPrimary, mb: 1 }}>
                      Ask {botLabel} a follow-up question
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        fullWidth size="small"
                        placeholder="e.g. What does this mean for my treatment?"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAskQuestion(); }}
                        disabled={asking}
                      />
                      <Button
                        variant="contained"
                        onClick={handleAskQuestion}
                        disabled={asking || !question.trim()}
                        sx={{ bgcolor: BRAND.navy, "&:hover": { bgcolor: BRAND.navyDark } }}
                      >
                        {asking ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                      </Button>
                    </Stack>
                    {asking && (
                      <Typography sx={{ fontSize: 12.5, color: textSecondary, mt: 1, fontStyle: "italic" }}>
                        {botLabel} is thinking…
                      </Typography>
                    )}
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {followUps.map((item, idx) => (
                        <Box key={idx} sx={{ borderRadius: "10px", border: `1px solid ${border}`, p: 1.5 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textSecondary, mb: 0.5 }}>
                            {item.question}
                          </Typography>
                          <Box sx={{ fontSize: 13.5, color: textPrimary }}>
                            <ReactMarkdown
                              components={markdownComponents(isDark, textPrimary, textSecondary, border, BRAND)}
                            >
                              {item.content}
                            </ReactMarkdown>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </motion.div>
            )}

            {/* Analysis result */}
            {answer && !analyzing && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {/* Result header bar */}
                  <Box sx={{
                    background: `linear-gradient(to right, ${BRAND.navyDark}, ${BRAND.navy})`,
                    px: 3, py: 1.75, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 2, flexWrap: "wrap",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                  }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Box sx={{
                        width: 42, height: 42, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
                        border: "2px solid rgba(99,124,219,0.55)",
                        boxShadow: "0 0 12px rgba(0,0,0,0.4), 0 0 0 1px rgba(99,124,219,0.15)",
                      }}>
                        <img
                          src={isPatient ? "/aiden-avatar.png" : "/claire-avatar.png"}
                          alt={botLabel}
                          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "50% 12%" }}
                        />
                      </Box>
                      <Box sx={{
                        width: 8, height: 8, borderRadius: "50%", bgcolor: "#22c55e",
                        boxShadow: "0 0 8px rgba(34,197,94,0.7)",
                        "@keyframes resultPulse": { "0%,100%": { boxShadow: "0 0 8px rgba(34,197,94,0.7)" }, "50%": { boxShadow: "0 0 16px rgba(34,197,94,0.4)" } },
                        animation: "resultPulse 2s infinite",
                      }} />
                      <Typography sx={{ fontFamily: "Michroma", fontSize: 15, fontWeight: 800, letterSpacing: "0.1em", color: "rgba(255,255,255,0.95)" }}>
                        {isPatient ? (
                          <><Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>den</>
                        ) : (
                          <>Cl<Box component="span" sx={{ color: BRAND.gold, fontWeight: 900 }}>AI</Box>re</>
                        )}{" · ANALYSIS COMPLETE"}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.55)" }}>{analysisTimestamp}</Typography>
                      {(() => {
                        const collDate = reportMetadata.find(m =>
                          ["coll. date","collection date","specimen date","collected"].some(k =>
                            m.field?.toLowerCase().replace(/[:.]/g,"").includes(k)
                          )
                        );
                        return collDate && collDate.value && collDate.value !== "Unknown" ? (
                          <Box sx={{
                            fontSize: 12, color: "rgba(255,255,255,0.7)",
                            bgcolor: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            px: 1, py: 0.25, borderRadius: "6px",
                            backdropFilter: "blur(4px)",
                          }}>
                            Collected: {collDate.value}
                          </Box>
                        ) : null;
                      })()}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {[
                        { icon: isSpeaking ? <VolumeOffIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />, onClick: handleToggleReadAloud, active: isSpeaking, title: isSpeaking ? "Stop reading" : "Read aloud" },
                        { icon: downloadingPdf ? <CircularProgress size={14} sx={{ color: BRAND.gold }} /> : <DownloadIcon sx={{ fontSize: 18 }} />, onClick: handleDownloadPDF, disabled: downloadingPdf, title: "Download PDF report" },
                        ...(history.length > 1 ? [{ icon: <HistoryIcon sx={{ fontSize: 18 }} />, onClick: () => setShowHistory(v => !v), active: showHistory, title: showHistory ? "Hide history" : "Show session history" }] : []),
                      ].map((btn, i) => (
                        <Tooltip key={i} title={btn.title}>
                          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                            <IconButton size="small" onClick={btn.onClick} disabled={btn.disabled} sx={{
                              color: btn.active ? BRAND.gold : "rgba(255,255,255,0.45)", p: 1,
                              borderRadius: "8px",
                              border: btn.active ? `1px solid ${BRAND.gold}44` : "1px solid transparent",
                              "&:hover": { color: BRAND.gold, bgcolor: "rgba(255,255,255,0.08)", border: `1px solid ${BRAND.gold}33` },
                              "&:disabled": { opacity: 0.3 },
                              transition: "all 0.18s",
                            }}>
                              {btn.icon}
                            </IconButton>
                          </motion.div>
                        </Tooltip>
                      ))}
                    </Stack>
                  </Box>

                  {/* Session history */}
                  <AnimatePresence>
                    {showHistory && history.length > 1 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <Box sx={{
                          bgcolor: isDark ? "rgba(7,13,31,0.8)" : "rgba(248,250,255,0.9)",
                          backdropFilter: "blur(12px)",
                          borderBottom: `1px solid ${border}`,
                          px: 3, py: 2, maxHeight: 260, overflowY: "auto", flexShrink: 0,
                        }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 800, color: textSecondary, letterSpacing: "0.15em", mb: 1.5 }}>
                            SESSION HISTORY
                          </Typography>
                          <Stack spacing={1.5}>
                            {history.map((item, idx) => (
                              <Box key={idx} sx={{ borderLeft: `3px solid ${idx === 0 ? BRAND.gold : border}`, pl: 2 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700,
                                  color: idx === 0 ? (isDark ? BRAND.gold : BRAND.navy) : textSecondary, mb: 0.25 }}>
                                  {idx === 0 ? "Initial Analysis" : `Q: ${item.type}`}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: textSecondary, lineHeight: 1.6,
                                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                  {item.content.replace(/[#*_`]/g, "").slice(0, 200)}…
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Metadata card */}
                  {reportMetadata.filter(m => m.field && m.value && m.value !== "Unknown").length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.15 }}
                    >
                      <Box sx={{
                        mx: { xs: 3, md: 5 }, mt: 3, mb: 0,
                        border: `1px solid ${border}`,
                        borderLeft: `4px solid ${BRAND.gold}`,
                        borderRadius: "14px",
                        bgcolor: isDark ? "rgba(0,37,96,0.12)" : "rgba(10,14,39,0.025)",
                        overflow: "hidden",
                        backdropFilter: "blur(8px)",
                        boxShadow: isDark
                          ? "0 4px 24px rgba(0,0,0,0.2)"
                          : "0 4px 24px rgba(10,14,39,0.06)",
                        transition: "box-shadow 0.3s",
                        "&:hover": {
                          boxShadow: isDark
                            ? "0 8px 32px rgba(0,0,0,0.3)"
                            : "0 8px 32px rgba(10,14,39,0.1)",
                        },
                      }}>
                        <Box sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${border}`,
                          display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: BRAND.gold }} />
                          <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.15em",
                            color: isDark ? BRAND.gold : BRAND.navy }}>
                            PATIENT INFORMATION
                          </Typography>
                        </Box>
                        <Box sx={{
                          px: 2.5, py: 2,
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
                          gap: "12px 20px",
                        }}>
                          {reportMetadata
                            .filter(m => m.field && m.value && m.value !== "Unknown")
                            .map((m, i) => (
                              <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + i * 0.05, duration: 0.3 }}
                              >
                                <Box>
                                  <Typography sx={{ fontSize: 10.5, fontWeight: 700,
                                    color: textSecondary, letterSpacing: "0.08em",
                                    textTransform: "uppercase", mb: 0.35 }}>
                                    {m.field}
                                  </Typography>
                                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: textPrimary }}>
                                    {m.value}
                                  </Typography>
                                </Box>
                              </motion.div>
                            ))}
                        </Box>
                      </Box>
                    </motion.div>
                  )}

                  {/* Analysis + Q&A thread */}
                  <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 3, md: 5 }, py: 4 }}>
                    <ReactMarkdown
                      components={markdownComponents(isDark, textPrimary, textSecondary, border, BRAND)}
                    >
                      {useAnimation ? typedAnswer : answer}
                    </ReactMarkdown>

                    {followUps.map((item, idx) => (
                      <Box key={idx}>
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 1.5 }}>
                            <Box sx={{
                              background: `linear-gradient(135deg, ${BRAND.navy}, #28336b)`,
                              color: "#fff",
                              px: 2, py: 1.25,
                              borderRadius: "14px 14px 3px 14px",
                              maxWidth: "78%", fontSize: 15, lineHeight: 1.6,
                              boxShadow: "0 4px 16px rgba(10,14,39,0.3)",
                            }}>
                              {item.question}
                            </Box>
                          </Box>
                        </motion.div>
                        <motion.div
                          initial={{ opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: 0.1 }}
                        >
                          <Box sx={{ borderLeft: `3px solid ${botAccent}`, pl: 2.5 }}>
                            <ReactMarkdown
                              components={markdownComponents(isDark, textPrimary, textSecondary, border, BRAND)}
                            >
                              {item.content}
                            </ReactMarkdown>
                          </Box>
                        </motion.div>
                      </Box>
                    ))}

                    {trendsData && trendsData.visit_count >= 2 && (
                      <TrendsPanel
                        charts={trendsData.charts}
                        visitCount={trendsData.visit_count}
                        isDark={isDark}
                        border={border}
                        textPrimary={textPrimary}
                        textSecondary={textSecondary}
                      />
                    )}
                  </Box>

                  {/* Sticky follow-up input */}
                  <Box sx={{
                    borderTop: `1px solid ${border}`,
                    px: { xs: 2, md: 4 }, py: 2,
                    bgcolor: isDark ? "rgba(7,13,31,0.9)" : "rgba(255,255,255,0.95)",
                    backdropFilter: "blur(16px)",
                    flexShrink: 0,
                  }}>
                    <Box sx={{
                      display: "flex", alignItems: "flex-end", gap: 1,
                      bgcolor: isDark ? "rgba(15,24,48,0.8)" : "#f4f7ff",
                      border: `1.5px solid ${border}`,
                      borderRadius: "16px", px: 1.75, py: 1,
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      "&:focus-within": {
                        borderColor: botAccent,
                        boxShadow: `0 0 0 4px ${botAccent}18`,
                      },
                    }}>
                      <TextField
                        fullWidth multiline maxRows={4} variant="standard"
                        placeholder={isPatient
                          ? "Ask a follow-up about your results…"
                          : "Ask a clinical follow-up question…"}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAskQuestion(); } }}
                        disabled={asking}
                        slotProps={{ input: { disableUnderline: true } }}
                        sx={{ "& textarea": { fontSize: 15, color: textPrimary, lineHeight: 1.6 } }}
                      />
                      <motion.div
                        whileHover={(!asking && question.trim()) ? { scale: 1.08 } : {}}
                        whileTap={(!asking && question.trim()) ? { scale: 0.92 } : {}}
                      >
                        <Box
                          onClick={(!asking && question.trim()) ? handleAskQuestion : undefined}
                          sx={{
                            width: 36, height: 36, borderRadius: "11px", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: (!asking && question.trim())
                              ? `linear-gradient(135deg, ${botAccent}, ${botAccent}cc)`
                              : (isDark ? "#1e2d50" : "#e2e6f0"),
                            color: (!asking && question.trim()) ? "#fff" : textSecondary,
                            cursor: (!asking && question.trim()) ? "pointer" : "default",
                            boxShadow: (!asking && question.trim()) ? `0 4px 16px ${botAccent}44` : "none",
                            transition: "all 0.2s ease",
                          }}>
                          {asking
                            ? <CircularProgress size={14} sx={{ color: "inherit" }} />
                            : <SendIcon sx={{ fontSize: 15 }} />}
                        </Box>
                      </motion.div>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            )}

          </AnimatePresence>
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        background: `linear-gradient(to right, ${BRAND.navyDark}, #05070f)`,
        py: 2, px: 3, flexShrink: 0,
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
          flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.35)", letterSpacing: "0.04em" }}>
            © {new Date().getFullYear()} Proove Genomics, Inc. · AI analysis is for informational purposes only.
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box
              component="a" href="/terms"
              sx={{ fontSize: 13, color: "rgba(99,124,219,0.7)", textDecoration: "none",
                "&:hover": { color: BRAND.gold }, cursor: "pointer" }}>
              Terms
            </Box>
            <Box
              component="a" href="/privacy"
              sx={{ fontSize: 13, color: "rgba(99,124,219,0.7)", textDecoration: "none",
                "&:hover": { color: BRAND.gold }, cursor: "pointer" }}>
              Privacy
            </Box>
          </Box>
          <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.18)", letterSpacing: "0.02em" }}>
            Powered by{" "}
            <Box component="a" href="https://labarium.com/" target="_blank" rel="noopener noreferrer"
              sx={{ color: "rgba(255,255,255,0.2)", textDecoration: "none", "&:hover": { color: "rgba(255,255,255,0.4)" } }}>
              Labarium Diagnostic Systems
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* ── Confirm modal ── */}
      <ConfirmModal
        open={confirmClearOpen}
        onConfirm={handleConfirmClear}
        onCancel={() => setConfirmClearOpen(false)}
        isDark={isDark}
      />

      {/* ── Toast notifications ── */}
      <ToastContainer toasts={toasts} onRemove={removeToast} isDark={isDark} />

      {/* ── Hidden printable report ── */}
      <Box sx={{ display: "none" }}>
        <PrintableLabReport
          ref={printRef}
          answer={answer}
          metadata={reportMetadata}
          botLabel={botLabel}
          trendsData={trendsData}
        />
      </Box>
    </Box>
  );
}
